import { Alert, Center, Drawer, Loader, Stack } from '@mantine/core'
import React, { Suspense, useMemo } from 'react'
import { usePagesWithSuspense, useWidgetsWithSuspense } from './api'
import {
  WidgetContextProvider,
  WidgetContextType,
  useEditMode,
} from './providers'
import { WidgetZod, Widgets, registry } from './registry'
import { WidgetEditorProvider, useWidgetEditor } from './state'
import { AddWidgetModal } from './AddWidgetModal'
import { IconAlertCircle } from '@tabler/icons-react'
import { CompetitionContextProvider } from './CompetitionContextProvider'

export function Page({
  path,
  children,
  context,
}: {
  path: string
  children: React.ReactNode
  context?: WidgetContextType
}) {
  return (
    <Suspense
      fallback={
        <Center w="100%" h="100%">
          <Loader />
        </Center>
      }
    >
      <PageContent path={path} context={context}>
        {children}
      </PageContent>
    </Suspense>
  )
}

function PageContent({
  path,
  children,
  context,
}: {
  path: string
  children: React.ReactNode
  context?: WidgetContextType
}) {
  // Fetch pages to find the page_id for the given path
  const { data: pages } = usePagesWithSuspense()
  const page = useMemo(() => {
    return pages.find((p) => p.path === path)
  }, [pages, path])

  const { data: widgets } = useWidgetsWithSuspense(page?.page_id || 0)

  const config = useMemo(() => {
    if (!page || !widgets) return {}

    return widgets.reduce(
      (acc, widget) => {
        const parsed = WidgetZod.safeParse(widget) // Validate widget config
        if (parsed.success) {
          acc[widget.id] = parsed.data
        } else {
          console.error(
            `Invalid widget config for id ${widget.id}:`,
            parsed.error
          )
          acc[widget.id] = widget // Fallback to original widget if validation fails
        }
        return acc
      },
      {} as Record<string, Widgets>
    )
  }, [page, widgets])

  if (!page) {
    return (
      <Stack p="xl" maw="600px">
        <Alert icon={<IconAlertCircle size="1rem" />} title="Error" color="red">
          Page content not found for "{path}". Please contact support.
        </Alert>
      </Stack>
    )
  }

  return (
    <WidgetEditorProvider initialConfig={config} pageId={page.page_id}>
      <CompetitionContextProvider>
        <WidgetContextProvider context={context || {}}>
          {children}
          <OptionsDrawer />
          <AddWidgetModal />
        </WidgetContextProvider>
      </CompetitionContextProvider>
    </WidgetEditorProvider>
  )
}

function OptionsDrawer() {
  const editor = useWidgetEditor()
  const isOptionsEditing = editor.useIsEditMode(null, 'options')
  const id = editor.useEditWidgetId()
  const [editMode] = useEditMode()
  const config = editor.useEditConfig()
  const Form = registry(config?.widget || '')?.formComponent

  return (
    <Drawer
      opened={editMode && isOptionsEditing}
      onClose={editor.endEditWidget}
      position="right"
      title="Edit Widget"
      overlayProps={{ display: 'none' }}
    >
      {id === null || Form == null ? null : <Form key={id} id={id} />}
    </Drawer>
  )
}
